import { create } from 'zustand';
import {
  TeacherProfile,
  AuthState,
  AuthCredentials,
  SignUpData,
  QuestionSet,
  QuestionFolder
} from '@/types';
import { authService } from '@/services/auth';
import logger from '@/utils/logger';

interface AuthStore extends AuthState {
  // Question Sets (teacher's created question sets)
  questionSets: QuestionSet[];
  isLoadingQuestionSets: boolean;

  // Folders for organizing question sets
  folders: QuestionFolder[];
  isLoadingFolders: boolean;
  currentFolderId: string | null; // Current folder being viewed (null = root)

  // Authentication Actions
  signIn: (credentials: AuthCredentials) => Promise<void>;
  signUp: (signUpData: SignUpData) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
  loadCurrentUser: () => Promise<void>;

  // Teacher Profile Actions
  loadTeacherProfile: () => Promise<void>;
  updateTeacherProfile: (updates: Partial<TeacherProfile>) => Promise<void>;

  // Question Set Actions
  loadQuestionSets: () => Promise<void>;
  createQuestionSet: (questionSet: Omit<QuestionSet, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateQuestionSet: (questionSetId: string, updates: Partial<QuestionSet>) => Promise<void>;
  deleteQuestionSet: (questionSetId: string) => Promise<void>;
  getQuestionSet: (questionSetId: string) => Promise<QuestionSet | null>;
  moveQuestionSetToFolder: (questionSetId: string, folderId: string | null) => Promise<void>;

  // Folder Actions
  loadFolders: () => Promise<void>;
  createFolder: (folder: Omit<QuestionFolder, 'id' | 'createdAt' | 'updatedAt'>) => Promise<string>;
  updateFolder: (folderId: string, updates: Partial<QuestionFolder>) => Promise<void>;
  deleteFolder: (folderId: string) => Promise<void>;
  moveFolder: (folderId: string, parentFolderId: string | null) => Promise<void>;
  setCurrentFolder: (folderId: string | null) => void;
  getFolderPath: (folderId: string | null) => QuestionFolder[];

  // Utility Actions
  sendPasswordResetEmail: (email: string) => Promise<void>;
  clearError: () => void;
  resetAuthState: () => void;

  // Internal cleanup
  _unsubscribeAuthState?: () => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  // Initial State
  user: null,
  teacherProfile: null,
  isLoading: false,
  isAuthenticated: false,
  error: null,
  questionSets: [],
  isLoadingQuestionSets: false,
  folders: [],
  isLoadingFolders: false,
  currentFolderId: null,

  // Authentication Actions
  signIn: async (credentials: AuthCredentials) => {
    try {
      set({ isLoading: true, error: null });
      console.log('🔐 Signing in user...');

      const user = await authService.signInWithEmailAndPassword(credentials);
      set({ user, isAuthenticated: true });

      // Load teacher profile after successful sign in
      await get().loadTeacherProfile();
      await get().loadQuestionSets();
      await get().loadFolders();

      console.log('✅ Sign in successful');
    } catch (error: unknown) {
      console.error('❌ Sign in failed:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error), isAuthenticated: false });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  signUp: async (signUpData: SignUpData) => {
    try {
      set({ isLoading: true, error: null });
      console.log('📝 Creating new account...');

      const user = await authService.signUpWithEmailAndPassword(signUpData);
      set({ user, isAuthenticated: true });

      // Load the newly created teacher profile
      await get().loadTeacherProfile();

      console.log('✅ Account created successfully');
    } catch (error: unknown) {
      console.error('❌ Sign up failed:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error), isAuthenticated: false });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  signInWithGoogle: async () => {
    try {
      set({ isLoading: true, error: null });
      console.log('🔐 Signing in with Google...');

      const user = await authService.signInWithGoogle();
      set({ user, isAuthenticated: true });

      // Load teacher profile after successful Google sign in
      await get().loadTeacherProfile();
      await get().loadQuestionSets();
      await get().loadFolders();

      console.log('✅ Google sign in successful');
    } catch (error: unknown) {
      console.error('❌ Google sign in failed:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error), isAuthenticated: false });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  signOut: async () => {
    try {
      set({ isLoading: true, error: null });
      console.log('👋 Signing out...');

      await authService.signOut();
      get().resetAuthState();

      console.log('✅ Sign out successful');
    } catch (error: unknown) {
      console.error('❌ Sign out failed:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  loadCurrentUser: async () => {
    try {
      set({ isLoading: true, error: null });

      const user = await authService.getCurrentUser();

      if (user) {
        set({ user, isAuthenticated: true });
        await get().loadTeacherProfile();
        await get().loadQuestionSets();
        await get().loadFolders();
      } else {
        set({ user: null, isAuthenticated: false });
      }
    } catch (error: unknown) {
      console.error('❌ Failed to load current user:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error), isAuthenticated: false });
    } finally {
      set({ isLoading: false });
    }
  },

  // Teacher Profile Actions
  loadTeacherProfile: async () => {
    try {
      const { user } = get();
      if (!user) {
        logger.debug('Skipping teacher profile load because no user is authenticated');
        return;
      }

      const teacherProfile = await authService.getTeacherProfile(user.uid);
      set({ teacherProfile });
    } catch (error: unknown) {
      console.error('❌ Failed to load teacher profile:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },

  updateTeacherProfile: async (updates: Partial<TeacherProfile>) => {
    try {
      const { user, teacherProfile } = get();
      if (!user || !teacherProfile) {
        throw new Error('No user or teacher profile available');
      }

      set({ isLoading: true, error: null });
      console.log('📝 Updating teacher profile...');

      await authService.updateTeacherProfile(user.uid, updates);

      // Update local state
      set({
        teacherProfile: { ...teacherProfile, ...updates, updatedAt: new Date() }
      });

      console.log('✅ Teacher profile updated');
    } catch (error: unknown) {
      console.error('❌ Failed to update teacher profile:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  // Question Set Actions
  loadQuestionSets: async () => {
    try {
      const { user } = get();
      if (!user) {
        logger.debug('Skipping question set load because no user is authenticated');
        return;
      }

      set({ isLoadingQuestionSets: true, error: null });

      const questionSets = await authService.getTeacherQuestionSets(user.uid);
      set({ questionSets });
    } catch (error: unknown) {
      console.error('❌ Failed to load question sets:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      set({ isLoadingQuestionSets: false });
    }
  },

  createQuestionSet: async (questionSetData: Omit<QuestionSet, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const { user } = get();
      if (!user) {
        throw new Error('No user available to create question set');
      }

      set({ isLoadingQuestionSets: true, error: null });
      console.log('📚 Creating question set...');

      const questionSetId = await authService.createQuestionSet(questionSetData);

      // Reload question sets to get the complete data
      await get().loadQuestionSets();

      console.log('✅ Question set created with ID:', questionSetId);
      return questionSetId;
    } catch (error: unknown) {
      console.error('❌ Failed to create question set:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingQuestionSets: false });
    }
  },

  updateQuestionSet: async (questionSetId: string, updates: Partial<QuestionSet>) => {
    try {
      set({ isLoadingQuestionSets: true, error: null });
      console.log('📝 Updating question set:', questionSetId);

      await authService.updateQuestionSet(questionSetId, updates);

      // Update local state
      set(state => ({
        questionSets: state.questionSets.map(qs =>
          qs.id === questionSetId
            ? { ...qs, ...updates, updatedAt: new Date() }
            : qs
        )
      }));

      console.log('✅ Question set updated');
    } catch (error: unknown) {
      console.error('❌ Failed to update question set:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingQuestionSets: false });
    }
  },

  deleteQuestionSet: async (questionSetId: string) => {
    try {
      set({ isLoadingQuestionSets: true, error: null });
      console.log('🗑️ Deleting question set:', questionSetId);

      await authService.deleteQuestionSet(questionSetId);

      // Remove from local state
      set(state => ({
        questionSets: state.questionSets.filter(qs => qs.id !== questionSetId)
      }));

      console.log('✅ Question set deleted');
    } catch (error: unknown) {
      console.error('❌ Failed to delete question set:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingQuestionSets: false });
    }
  },

  getQuestionSet: async (questionSetId: string) => {
    try {
      console.log('📚 Fetching question set:', questionSetId);
      const questionSet = await authService.getQuestionSet(questionSetId);
      console.log(questionSet ? '✅ Question set found' : '❌ Question set not found');
      return questionSet;
    } catch (error: unknown) {
      console.error('❌ Failed to get question set:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    }
  },

  moveQuestionSetToFolder: async (questionSetId: string, folderId: string | null) => {
    try {
      set({ isLoadingQuestionSets: true, error: null });
      console.log('📦 Moving question set to folder:', folderId || 'root');

      await authService.moveQuestionSetToFolder(questionSetId, folderId);

      // Update local state
      set(state => ({
        questionSets: state.questionSets.map(qs =>
          qs.id === questionSetId
            ? { ...qs, folderId: folderId || undefined, updatedAt: new Date() }
            : qs
        )
      }));

      console.log('✅ Question set moved successfully');
    } catch (error: unknown) {
      console.error('❌ Failed to move question set:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingQuestionSets: false });
    }
  },

  // Folder Actions
  loadFolders: async () => {
    try {
      const { user } = get();
      if (!user) {
        logger.debug('Skipping folder load because no user is authenticated');
        return;
      }

      set({ isLoadingFolders: true, error: null });

      const folders = await authService.getTeacherFolders(user.uid);
      set({ folders });
    } catch (error: unknown) {
      console.error('❌ Failed to load folders:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
    } finally {
      set({ isLoadingFolders: false });
    }
  },

  createFolder: async (folderData: Omit<QuestionFolder, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      const { user } = get();
      if (!user) {
        throw new Error('No user available to create folder');
      }

      set({ isLoadingFolders: true, error: null });
      console.log('📁 Creating folder...');

      const folderId = await authService.createFolder(folderData);

      // Reload folders to get the complete data
      await get().loadFolders();

      console.log('✅ Folder created with ID:', folderId);
      return folderId;
    } catch (error: unknown) {
      console.error('❌ Failed to create folder:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingFolders: false });
    }
  },

  updateFolder: async (folderId: string, updates: Partial<QuestionFolder>) => {
    try {
      set({ isLoadingFolders: true, error: null });
      console.log('📝 Updating folder:', folderId);

      await authService.updateFolder(folderId, updates);

      // Update local state
      set(state => ({
        folders: state.folders.map(f =>
          f.id === folderId
            ? { ...f, ...updates, updatedAt: new Date() }
            : f
        )
      }));

      console.log('✅ Folder updated');
    } catch (error: unknown) {
      console.error('❌ Failed to update folder:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingFolders: false });
    }
  },

  deleteFolder: async (folderId: string) => {
    try {
      set({ isLoadingFolders: true, error: null });
      console.log('🗑️ Deleting folder:', folderId);

      await authService.deleteFolder(folderId);

      // Remove from local state
      set(state => ({
        folders: state.folders.filter(f => f.id !== folderId),
        currentFolderId: state.currentFolderId === folderId ? null : state.currentFolderId
      }));

      console.log('✅ Folder deleted');
    } catch (error: unknown) {
      console.error('❌ Failed to delete folder:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingFolders: false });
    }
  },

  moveFolder: async (folderId: string, parentFolderId: string | null) => {
    try {
      set({ isLoadingFolders: true, error: null });
      console.log('📁 Moving folder to parent:', parentFolderId || 'root');

      await authService.moveFolderToFolder(folderId, parentFolderId);

      // Update local state
      set(state => ({
        folders: state.folders.map(f =>
          f.id === folderId
            ? { ...f, parentFolderId: parentFolderId || undefined, updatedAt: new Date() }
            : f
        )
      }));

      console.log('✅ Folder moved successfully');
    } catch (error: unknown) {
      console.error('❌ Failed to move folder:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoadingFolders: false });
    }
  },

  setCurrentFolder: (folderId: string | null) => {
    console.log('📂 Setting current folder:', folderId || 'root');
    set({ currentFolderId: folderId });
  },

  getFolderPath: (folderId: string | null): QuestionFolder[] => {
    const { folders } = get();
    const path: QuestionFolder[] = [];

    let currentId = folderId;
    const visited = new Set<string>();

    while (currentId) {
      if (visited.has(currentId)) {
        console.warn('⚠️ Circular reference detected in folder path');
        break;
      }
      visited.add(currentId);

      const folder = folders.find(f => f.id === currentId);
      if (!folder) break;

      path.unshift(folder); // Add to beginning to build path from root
      currentId = folder.parentFolderId || null;
    }

    return path;
  },

  // Utility Actions
  sendPasswordResetEmail: async (email: string) => {
    try {
      set({ isLoading: true, error: null });
      console.log('📧 Sending password reset email...');

      await authService.sendPasswordResetEmail(email);

      console.log('✅ Password reset email sent');
    } catch (error: unknown) {
      console.error('❌ Failed to send password reset email:', error instanceof Error ? error.message : String(error));
      set({ error: error instanceof Error ? error.message : String(error) });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  clearError: () => {
    set({ error: null });
  },

  resetAuthState: () => {
    const { _unsubscribeAuthState } = get();

    // Cleanup auth state listener
    if (_unsubscribeAuthState) {
      _unsubscribeAuthState();
    }

    set({
      user: null,
      teacherProfile: null,
      isLoading: false,
      isAuthenticated: false,
      error: null,
      questionSets: [],
      isLoadingQuestionSets: false,
      folders: [],
      isLoadingFolders: false,
      currentFolderId: null,
      _unsubscribeAuthState: undefined
    });
  }
}));

// Initialize auth state listener when the store is created
const initializeAuthStateListener = () => {
  const unsubscribe = authService.onAuthStateChanged((user) => {
    const store = useAuthStore.getState();

    if (user && !store.isAuthenticated) {
      // User signed in from another tab/window
      logger.debug('External sign in detected, refreshing auth state');
      store.loadCurrentUser();
    } else if (!user && store.isAuthenticated) {
      // User signed out from another tab/window
      logger.debug('External sign out detected, clearing auth state');
      store.resetAuthState();
    }
  });

  // Store the unsubscribe function
  useAuthStore.setState({ _unsubscribeAuthState: unsubscribe });
};

// Initialize the listener when the module loads
initializeAuthStateListener();

/**
 * Authentication Store - Manages teacher authentication and profile data
 *
 * Key Features:
 * - State Synchronization: Automatically syncs with Firebase auth state changes
 * - Profile Management: Teacher profiles are loaded when users sign in
 * - Error Boundary: Comprehensive error handling with proper cleanup
 */
